import cx from "classnames"
import Icon from "src/shared/icons"
import Tooltip from "src/shared/components/data-display/tooltip/Tooltip"
import { getCurrencyFormat } from "src/utils/string"
import { ComponentProps } from "src/types/component"
import {
    CardGroupProps,
    CardHeaderProps,
    CardLinkProps,
    CardMetricProps,
    CardMetricVariantProps,
    CardPercentProps,
    CardRowProps,
    CardTooltip,
    ComposableCardProps,
} from "./ComposableCard.types"
import "./ComposableCard.css"

/** El icono "info" no trae viewBox, se declara para poder escalarlo sin recortarlo */
const TOOLTIP_ICON_SIZE = 16
const TOOLTIP_ICON_VIEWBOX = "0 0 20 21"

function CardTooltipIcon({ tooltip }: { tooltip?: CardTooltip }): JSX.Element | null {
    if (!tooltip?.title) {
        return null
    }

    return (
        <div className="composable-card__tooltip">
            <Tooltip title={tooltip.title} description={tooltip.description} theme="dark">
                <Icon
                    name={"info"}
                    width={TOOLTIP_ICON_SIZE}
                    height={TOOLTIP_ICON_SIZE}
                    viewBox={TOOLTIP_ICON_VIEWBOX}
                />
            </Tooltip>
        </div>
    )
}

/** Encabezado suelto: solo titulo (+ tooltip) o children libres */
function CardHeader({ className = "", style = {}, title = "", tooltip, children }: CardHeaderProps): JSX.Element {
    return (
        <div className={cx("composable-card__header", className)} style={style}>
            {children ?? <p className="composable-card__title">{title}</p>}
            <CardTooltipIcon tooltip={tooltip} />
        </div>
    )
}

/**
 * Bloque titulo + valor. Variantes:
 * - default: titulo 12px + valor 22px
 * - compact: valor morado 14px + descripcion 10px, alineados a la derecha
 * - highlight: titulo + valor rojo 20px
 */
function CardMetric({
    className = "",
    style = {},
    title = "",
    value = "",
    description = "",
    tooltip,
    variant = "default",
    money = true,
    align,
    reverse = false,
    onValueClick,
    valueClassName = "",
    titleClassName = "",
}: CardMetricProps): JSX.Element {
    const formattedValue = money ? getCurrencyFormat(value) : value
    const alignment = align ?? (variant === "compact" ? "right" : "left")

    const titleNode = title ? (
        <div className="composable-card__metric-header" key="title">
            <p className={cx("composable-card__title", titleClassName)}>{title}</p>
            <CardTooltipIcon tooltip={tooltip} />
        </div>
    ) : (
        <CardTooltipIcon tooltip={tooltip} key="title" />
    )

    const valueNode = (
        <p
            key="value"
            className={cx(
                "composable-card__value",
                {
                    "composable-card__value_variant_compact": variant === "compact",
                    "composable-card__value_variant_highlight": variant === "highlight",
                    "composable-card__value_variant_link": variant === "link",
                    "composable-card__value_clickable": !!onValueClick,
                },
                valueClassName
            )}
            onClick={onValueClick}
        >
            {formattedValue}
        </p>
    )

    return (
        <div className={cx("composable-card__metric", `composable-card_align_${alignment}`, className)} style={style}>
            {reverse ? [valueNode, titleNode] : [titleNode, valueNode]}
            {description && <p className="composable-card__description">{description}</p>}
        </div>
    )
}

/** Valor morado 14px + descripcion 10px */
function CardCompactMetric(props: CardMetricVariantProps): JSX.Element {
    return <CardMetric {...props} variant="compact" />
}

/** Titulo + valor rojo 20px */
function CardHighlightMetric(props: CardMetricVariantProps): JSX.Element {
    return <CardMetric {...props} variant="highlight" />
}

function CardPercent({ className = "", style = {}, value = "", label = "del total" }: CardPercentProps): JSX.Element {
    const percent = parseFloat(`${value}`.replace("%", ""))

    if (isNaN(percent)) {
        return <></>
    }

    return (
        <div className={cx("composable-card__percent", className)} style={style}>
            <p className="composable-card__percent-value">{percent.toFixed(2)}%</p>
            {label && <p className="composable-card__percent-label">{label}</p>}
        </div>
    )
}

function CardLink({ className = "", style = {}, label, onClick }: CardLinkProps): JSX.Element {
    return (
        <p className={cx("composable-card__link", className)} style={style} onClick={onClick ?? (() => null)}>
            {label}
        </p>
    )
}

function CardRow({ className = "", style = {}, children, justify = "between", align = "center", gap }: CardRowProps) {
    return (
        <div
            className={cx(
                "composable-card__row",
                `composable-card_justify_${justify}`,
                `composable-card_align_${align}`,
                className
            )}
            style={{ ...(gap !== undefined ? { gap } : {}), ...style }}
        >
            {children}
        </div>
    )
}

function CardGroup({ className = "", style = {}, children, align = "left", gap }: CardGroupProps): JSX.Element {
    return (
        <div
            className={cx("composable-card__group", `composable-card_align_${align}`, className)}
            style={{ ...(gap !== undefined ? { gap } : {}), ...style }}
        >
            {children}
        </div>
    )
}

function CardFooter({ className = "", style = {}, children, align = "left", gap }: CardGroupProps): JSX.Element {
    return (
        <CardGroup className={cx("composable-card__footer", className)} style={style} align={align} gap={gap}>
            {children}
        </CardGroup>
    )
}

function CardDivider({ className = "", style = {} }: ComponentProps): JSX.Element {
    return <div className={cx("composable-card__divider", className)} style={style} />
}

function CardRoot({
    className = "",
    containerClassName = "",
    style = {},
    children,
    onClick,
}: ComposableCardProps): JSX.Element {
    return (
        <div className={cx("composable-card__container", containerClassName)}>
            <div
                className={cx("composable-card", { "composable-card_clickable": !!onClick }, className)}
                style={style}
                onClick={onClick}
            >
                {children}
            </div>
        </div>
    )
}

const Metric = Object.assign(CardMetric, {
    Compact: CardCompactMetric,
    Highlight: CardHighlightMetric,
})

const ComposableCard = Object.assign(CardRoot, {
    Header: CardHeader,
    Metric,
    /** Alias directos de las variantes de Metric */
    Value: CardMetric,
    Compact: CardCompactMetric,
    Highlight: CardHighlightMetric,
    Percent: CardPercent,
    Link: CardLink,
    Row: CardRow,
    Group: CardGroup,
    Footer: CardFooter,
    Divider: CardDivider,
})

export default ComposableCard
